import React, { useState, useEffect } from 'react'
import { useMedications } from '../../hooks/useMedications'

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  status: 'completed' | 'partial' | 'missed' | 'scheduled' | 'today-pending' | null
  activities: number
}

interface MonthlyCalendarProps {
  onDayClick?: (date: Date) => void
}

const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({ onDayClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth())
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [calendarData, setCalendarData] = useState<CalendarDay[]>([])
  const [loading, setLoading] = useState(false)
  
  const { getMonthlyHistory, shouldTakeToday, medications, debugActivities, takeMedication, isMedicationActiveOnDate, shouldTakeOnDate } = useMedications()

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ]

  const dayHeaders = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

  const getDayStatus = (date: Date, activities: any[]): CalendarDay['status'] => {
    const today = new Date()
    const isToday = date.toDateString() === today.toDateString()
    
    // If future date, return scheduled if there are medications for that day
    if (date > today) {
      const dayMedications = medications.filter(med => 
        isMedicationActiveOnDate(med, date) && shouldTakeOnDate(med, date)
      )
      return dayMedications.length > 0 ? 'scheduled' : null
    }
    
    // Calculate expected medications for this specific date
    // Filter by both active on date (createdAt check) and schedule (shouldTakeOnDate)
    const dayMedications = medications.filter(med => 
      isMedicationActiveOnDate(med, date) && shouldTakeOnDate(med, date)
    )
    let expectedTakes = 0
    
    dayMedications.forEach(med => {
      const times = med.times || []
      expectedTakes += times.length === 0 ? 1 : times.length
    })
    
    const takenActivities = activities.filter(activity => 
      activity.status === 'taken' || 
      (activity.type === 'medication_taken' && activity.status !== 'missed')
    )
    const takenCount = takenActivities.length
    
    console.log(`📊 Status calculation for ${date.toDateString()}: expected=${expectedTakes}, taken=${takenCount}, medications=${dayMedications.length}`)
    
    if (expectedTakes === 0) return null
    
    if (isToday) {
      if (takenCount === 0) return 'today-pending'
      if (takenCount < expectedTakes) return 'partial'
      return 'completed'
    }
    
    // Past dates
    if (takenCount === 0) return 'missed'
    if (takenCount < expectedTakes) return 'partial'
    return 'completed'
  }

  const getStatusColor = (status: CalendarDay['status']): string => {
    switch (status) {
      case 'completed': return 'bg-green-500'
      case 'partial': return 'bg-yellow-500'
      case 'missed': return 'bg-red-500'
      case 'today-pending': return 'bg-blue-500'
      case 'scheduled': return 'bg-gray-400'
      default: return ''
    }
  }

  const generateCalendarDays = async (): Promise<CalendarDay[]> => {
    const firstDay = new Date(currentYear, currentMonth, 1)
    const startDate = new Date(firstDay)
    
    // Start from Sunday of the week containing the first day
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    // Calculate end date (6 weeks from start)
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + 41)
    endDate.setHours(23, 59, 59, 999)
    
    console.log('📅 Loading calendar data from', startDate.toDateString(), 'to', endDate.toDateString())
    
    const days: CalendarDay[] = []
    
    // Load activities for the entire calendar view range (including previous/next month days)
    let allActivities: any[] = []
    
    // Get activities for the main month
    const mainMonthActivities = await getMonthlyHistory(currentYear, currentMonth)
    allActivities = [...mainMonthActivities]
    
    // If start date is in previous month, get those activities too
    if (startDate.getMonth() !== currentMonth || startDate.getFullYear() !== currentYear) {
      const prevMonth = startDate.getMonth()
      const prevYear = startDate.getFullYear()
      const prevMonthActivities = await getMonthlyHistory(prevYear, prevMonth)
      allActivities = [...allActivities, ...prevMonthActivities]
    }
    
    // If end date is in next month, get those activities too
    if (endDate.getMonth() !== currentMonth || endDate.getFullYear() !== currentYear) {
      const nextMonth = endDate.getMonth()
      const nextYear = endDate.getFullYear()
      const nextMonthActivities = await getMonthlyHistory(nextYear, nextMonth)
      allActivities = [...allActivities, ...nextMonthActivities]
    }
    
    console.log('📊 Total activities loaded for calendar:', allActivities.length)
    
    // Group activities by date
    const activitiesByDate = allActivities.reduce((acc, activity) => {
      const activityDate = activity.actualTime || activity.timestamp || new Date()
      const dateKey = activityDate.toDateString()
      if (!acc[dateKey]) acc[dateKey] = []
      acc[dateKey].push(activity)
      return acc
    }, {} as Record<string, any[]>)
    
    console.log('📅 Activities grouped by date:', Object.keys(activitiesByDate))
    
    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)
      
      const isCurrentMonth = date.getMonth() === currentMonth
      const isToday = date.toDateString() === new Date().toDateString()
      const dayActivities = activitiesByDate[date.toDateString()] || []
      
      // Calculate status for ALL days, not just current month
      const status = getDayStatus(date, dayActivities)
      
      // Log only if there are activities or status is not null
      if (dayActivities.length > 0 || status) {
        console.log(`📅 Day ${date.toDateString()}: ${dayActivities.length} activities, status: ${status}`)
      }
      
      days.push({
        date,
        isCurrentMonth,
        isToday,
        status,
        activities: dayActivities.length
      })
    }
    
    return days
  }

  const loadCalendarData = async () => {
    setLoading(true)
    try {
      const days = await generateCalendarDays()
      setCalendarData(days)
    } catch (error) {
      console.error('Error loading calendar data:', error)
    } finally {
      setLoading(false)
    }
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11)
        setCurrentYear(currentYear - 1)
      } else {
        setCurrentMonth(currentMonth - 1)
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0)
        setCurrentYear(currentYear + 1)
      } else {
        setCurrentMonth(currentMonth + 1)
      }
    }
  }

  const handleDayClick = (day: CalendarDay) => {
    if (onDayClick) {
      onDayClick(day.date)
    }
  }

  useEffect(() => {
    loadCalendarData()
  }, [currentMonth, currentYear, medications])

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-7 gap-2 mb-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {[...Array(42)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {monthNames[currentMonth]} {currentYear}
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {dayHeaders.map((day) => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {calendarData.map((day, index) => (
          <div
            key={index}
            onClick={() => handleDayClick(day)}
            className={`
              aspect-square flex items-center justify-center text-sm relative cursor-pointer rounded-md transition-colors
              ${day.isCurrentMonth 
                ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700' 
                : 'text-gray-300 dark:text-gray-600'
              }
              ${day.isToday 
                ? 'bg-primary text-white font-semibold hover:bg-primary/90' 
                : ''
              }
            `}
          >
            <span className="z-10">{day.date.getDate()}</span>
            
            {/* Status indicator */}
            {day.status && (
              <div 
                className={`absolute top-1 right-1 w-2 h-2 rounded-full ${getStatusColor(day.status)}`}
                title={day.status}
              />
            )}
            
            {/* Activity count */}
            {day.activities > 0 && (
              <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-300">Completado</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-300">Parcial</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-300">Perdido</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-300">Hoy pendiente</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
            <span className="text-gray-600 dark:text-gray-300">Programado</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MonthlyCalendar 